import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import type { UploadTaskSnapshot } from 'firebase/storage';
import type { FirebaseStorageError } from '@/types';
import { storage } from './config';
import { cropToSquare } from '@/lib/utils/imageOptimization';

/**
 * Firebase Storage service for Digital Menu SaaS
 * Handles file uploads, downloads, and management for menu item images
 */

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  percentage: number;
}

export interface UploadResult {
  downloadURL: string;
  path: string;
  size: number;
}

/**
 * Upload a file to Firebase Storage
 */
export const uploadFile = async (
  file: File,
  path: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> => {
  try {
    const storageRef = ref(storage, path);
    
    if (onProgress) {
      // Use resumable upload for progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot: UploadTaskSnapshot) => {
            const progress = {
              bytesTransferred: snapshot.bytesTransferred,
              totalBytes: snapshot.totalBytes,
              percentage: (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
            };
            onProgress(progress);
          },
          (error) => {
            console.error('Upload error:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                downloadURL,
                path,
                size: file.size,
              });
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    } else {
      // Simple upload without progress tracking
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return {
        downloadURL,
        path,
        size: file.size,
      };
    }
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Upload a menu item image with square cropping and optimization
 */
export const uploadMenuItemImage = async (
  file: File,
  restaurantId: string,
  menuItemId: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> => {
  // First crop to square and convert to WebP (cropToSquare already converts to WebP)
  const croppedFile = await cropToSquare(file);
  
  // Generate unique filename to prevent conflicts
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  const fileName = `menu-item-${menuItemId}-${timestamp}-${randomSuffix}.webp`; // Always WebP after cropping
  const path = `restaurants/${restaurantId}/menu-items/${fileName}`;
  
  return uploadFile(croppedFile, path, onProgress);
};

/**
 * Upload a restaurant logo with square cropping
 */
export const uploadRestaurantLogo = async (
  file: File,
  restaurantId: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> => {
  // First crop to square and convert to WebP (cropToSquare already converts to WebP)
  const croppedFile = await cropToSquare(file);
  const fileName = `logo.webp`; // Always use .webp extension
  const path = `restaurants/${restaurantId}/${fileName}`;
  
  return uploadFile(croppedFile, path, onProgress);
};

/**
 * Delete a file from Firebase Storage
 */
export const deleteFile = async (path: string): Promise<void> => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    console.log(`Successfully deleted file: ${path}`);
  } catch (error: unknown) {
    // Silently ignore "object not found" errors - this is expected for files that don't exist
    const storageError = error as FirebaseStorageError;
    if (storageError?.code === 'storage/object-not-found') {
      console.log(`File not found (this is normal): ${path}`);
      return; // Don't throw error for missing files
    }
    
    // Log and throw other errors
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * Get download URL for a file
 */
export const getFileURL = async (path: string): Promise<string> => {
  try {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error('Error getting file URL:', error);
    throw error;
  }
};

/**
 * Delete a menu item image
 */
export const deleteMenuItemImage = async (
  restaurantId: string,
  fileName: string
): Promise<void> => {
  const path = `restaurants/${restaurantId}/menu-items/${fileName}`;
  return deleteFile(path);
};

/**
 * Extract file name from Firebase Storage URL
 */
export const extractFileNameFromUrl = (url: string): string | null => {
  try {
    // Firebase Storage URLs contain the full path in the format:
    // https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{fullPath}?{params}
    const urlParts = url.split('/o/')[1]; // Get everything after '/o/'
    if (!urlParts) return null;
    
    const pathWithParams = urlParts.split('?')[0]; // Remove query parameters
    const decodedPath = decodeURIComponent(pathWithParams);
    
    // Extract just the filename from the full path
    // Path format: restaurants/{restaurantId}/menu-items/{fileName}
    const pathSegments = decodedPath.split('/');
    return pathSegments[pathSegments.length - 1]; // Just the filename
  } catch (error) {
    console.error('Error extracting filename from URL:', error);
    return null;
  }
};
export const validateImageFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Please upload a valid image file (JPEG, PNG, or WebP)',
    };
  }
  
  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'File size must be less than 5MB',
    };
  }
  
  return { isValid: true };
};

// Export storage instance for direct use if needed
export { storage };
